import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  StepCard, StepOptions, WorkflowView, createAutomationWorkflow, getAutomationWorkflow, getStepOptions,
  updateAutomationWorkflow,
} from '../../../api/cafAutomation';
import { CatalogueReviews } from '../../../api/emailCatalogue';
import WorkflowEmailAdoptionReview from '../../email-catalogue/WorkflowEmailAdoptionReview';
import { formatCount } from '../format';
import { Icon } from '../Icon';
import { Modal } from '../Overlay';
import { MSG_NOT_CONFIRMED, isClash, runSave } from '../saveEngine';
import { useToast } from '../ToastContext';
import { STATE_LABEL, STATE_TONE } from './automationCopy';
import { EmailPreviewModal } from './EmailPreviewModal';
import { NoteField, SaveProblem } from './formBits';
import { StepChips } from './StepChips';
import { StepEditor } from './StepEditor';
import {
  catalogueReviewedFor, checkedCount, clientEmailCount, listsOffered, reviewedFor, stepIndexFromPath, stepProblems, stepSentence,
  suggestName,
} from './steps';

interface Props {
  readOnly?: boolean;
  workflowId: string | null;
  onClose: () => void;
  onSaved: (view: WorkflowView | null) => void;
  onOpenTeach: () => void;
}

const PRIMARY: Record<string, { label: string; note: string }> = {
  new: { label: 'Save and start watching', note: 'New workflows start in Watching. They record what they would have done and send nothing until you turn them on.' },
  watching: { label: 'Save', note: 'It keeps watching: it records what it would have done and sends nothing.' },
  on: { label: 'Save new version', note: 'The current version keeps running until you approve this one.' },
};

export const WorkflowWindow: React.FC<Props> = ({ workflowId, onClose, onSaved, onOpenTeach, readOnly: userReadOnly = false }) => {
  const id = useId();
  const toast = useToast();
  const [options, setOptions] = useState<StepOptions | null>(null);
  const [view, setView] = useState<WorkflowView | null>(null);
  const [adoption, setAdoption] = useState<{ version: number | null } | null>(null);
  const [adoptionVersion, setAdoptionVersion] = useState<string>('draft');
  const [loadProblem, setLoadProblem] = useState('');
  const [cards, setCardsRaw] = useState<StepCard[]>([{ kind: 'attach' }]);
  const [refusal, setRefusal] = useState<{ index: number | null; message: string } | null>(null);
  const [reviewedCatalogue, setReviewedCatalogue] = useState<CatalogueReviews>({});
  const [reviewed, setReviewed] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [note, setNote] = useState('');
  const [step, setStep] = useState<1 | 2>(1);
  const [preview, setPreview] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [adoptionBusy, setAdoptionBusy] = useState(false);
  const adoptionOwner = useRef<symbol | null>(null);
  const busy = saving || adoptionBusy;
  const beginAdoption = () => {
    if (saving || adoptionOwner.current) return null;
    const owner = Symbol('adoption'); adoptionOwner.current = owner; setAdoptionBusy(true);
    return () => { if (adoptionOwner.current === owner) { adoptionOwner.current = null; setAdoptionBusy(false); } };
  };
  const [problem, setProblem] = useState('');
  /** A create whose outcome is unknown: saving again could make a duplicate, so only Reload (close and re-list) is offered. */
  const [lostCreate, setLostCreate] = useState(false);
  const setCards = useCallback((next: StepCard[]) => {
    if (adoptionOwner.current) return;
    if (refusal) setProblem('');
    setRefusal(null);
    setCardsRaw(next);
    setReviewedCatalogue({});
    // Review keys bind exact settings and graph positions. Any list edit must
    // be checked again; template-source review alone cannot approve a forward.
    setReviewed((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith('forward:'))));
  }, [refusal]);
  const heading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (heading.current) heading.current.focus();
  }, [step]);
  const saveRef = useRef<(asDraft: boolean) => Promise<void>>(async () => undefined);
  const [loaded, setLoaded] = useState(workflowId === null);

  useEffect(() => {
    let alive = true;
    getStepOptions().then((next) => { if (alive) setOptions(next); }, () => { if (alive) setOptions(null); });
    return () => { alive = false; };
  }, []);

  /** First load fills the form; a Reload after a clash only refreshes the revision, keeping what was typed. */
  const loadWorkflow = useCallback((fill: boolean) => {
    if (!workflowId) return;
    return getAutomationWorkflow(workflowId).then(
      (next) => {
        setView(next);
        if (fill) {
          if (next.steps) setCardsRaw(next.steps);
          setReviewed(next.checked_templates || {});
          setReviewedCatalogue(next.checked_catalogue || {});
          setName(next.name);
          setNameTouched(true);
        }
        setLoaded(true);
      },
      () => setLoadProblem("Couldn't open this workflow. Close and try again."),
    );
  }, [workflowId]);
  useEffect(() => { loadWorkflow(true); }, [loadWorkflow]);

  const state = view ? view.state : 'new';
  const primary = state === 'on' ? PRIMARY.on : state === 'watching' ? PRIMARY.watching : PRIMARY.new;
  const canDraft = !view || view.state === 'draft';
  const problems = stepProblems(cards, listsOffered(options) ?? true);
  const emails = clientEmailCount(cards);
  const checked = checkedCount(cards, options, reviewed, reviewedCatalogue);
  const shownName = nameTouched ? name : suggestName(cards, listsOffered(options));
  const stepsOk = problems.length === 0;
  const readOnly = userReadOnly || Boolean(view && view.read_only);
  const dirty = !!view && (JSON.stringify(cards) !== JSON.stringify(view.steps || cards) || shownName !== view.name);

  const save = async (asDraft: boolean) => {
    if (readOnly || adoptionOwner.current) return;
    const body = {
      name: shownName.trim(), steps: cards, reviewed_emails: reviewedFor(cards, options, reviewed),
      ...(Object.keys(reviewedCatalogue).length ? { reviewed_catalogue: catalogueReviewedFor(cards, reviewedCatalogue) } : {}),
      expected_revision: view ? view.revision : 0, ...(note.trim() ? { note: note.trim() } : {}), as_draft: asDraft,
    };
    setSaving(true);
    setProblem('');
    // The saveEngine keeps only the message, so note a step refusal's path on the way past.
    let stepPath: unknown = null;
    let held = false;
    const result = await runSave(async () => {
      try {
        return await (view ? updateAutomationWorkflow(view.workflow_id, body) : createAutomationWorkflow(body));
      } catch (err) {
        const response = (err as { response?: { status?: number; data?: { path?: unknown } } }).response;
        if (response && response.status === 422) held = true;
        if (response && response.status === 400 && typeof response.data?.path === 'string') stepPath = response.data.path;
        throw err;
      }
    });
    setSaving(false);
    if (!result.ok) {
      setProblem(result.message);
      if (typeof stepPath === 'string' && stepPath.indexOf('steps') === 0) {
        // Keep the ticks; the hash-aware preview flags any email that changed.
        setRefusal({ index: stepIndexFromPath(stepPath), message: result.message });
        setStep(1);
      }
      // A clash or an unconfirmed save is never resent blind: Reload first. Steps and holds would fail the same way.
      if (result.message === MSG_NOT_CONFIRMED && !view) setLostCreate(true);
      const again = !isClash(result.message) && result.message !== MSG_NOT_CONFIRMED && !held && stepPath === null;
      // Try again resends the latest form, not the one this attempt closed over.
      if (again) toast.failed(result.message, () => { void saveRef.current(asDraft); });
      return;
    }
    toast.saved(asDraft ? 'Saved as a draft.' : view ? 'Saved.' : "Saved. It's watching now. Attach it to examples in Teach the AI.");
    onSaved(result.value);
    onClose();
  };
  saveRef.current = save;

  const header = (
    <div className="cur-inline">
      <nav className="cur-stepper" aria-label="Steps of the workflow window">
        <button type="button" aria-current={step === 1 ? 'step' : undefined} disabled={busy} onClick={() => setStep(1)}>1 What should happen?</button>
        <Icon name="chev-r" size="xs" />
        <button type="button" aria-current={step === 2 ? 'step' : undefined} disabled={!stepsOk || readOnly || busy}
          onClick={() => setStep(2)}>2 Check and save</button>
      </nav>
      {view && <span className={`cur-pill cur-pill--${STATE_TONE[view.state]}`}>{STATE_LABEL[view.state]}</span>}
    </div>
  );

  const footer = readOnly ? (
    <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={busy}>Close</button>
  ) : step === 1 ? (
    <>
      <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose} disabled={busy}>Cancel</button>
      <button type="button" className="ui-btn ui-btn--primary ui-btn--md" disabled={busy || !stepsOk || !loaded} onClick={() => setStep(2)}>
        Next: check and save
      </button>
    </>
  ) : (
    <>
      <span className="cur-hint cur-grow">{primary.note}</span>
      {canDraft && (
        <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" disabled={busy || lostCreate || !stepsOk || !shownName.trim()}
          onClick={() => { void save(true); }}>Save draft</button>
      )}
      <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={() => setStep(1)} disabled={busy}>Back</button>
      <button type="button" className="ui-btn ui-btn--primary ui-btn--md"
        disabled={busy || lostCreate || !stepsOk || !shownName.trim() || checked < emails} onClick={() => { void save(false); }}>
        {saving ? <><span className="cur-spin" aria-hidden="true" />Saving…</> : primary.label}
      </button>
    </>
  );

  return (
    <Modal title={view ? view.name : 'New workflow'} size="xwide" onClose={onClose} busy={busy} footer={footer}>
      {header}
      {loadProblem && <div className="cur-alert cur-alert--error" role="alert">{loadProblem}</div>}
      {options?.catalogue_blockers?.map(code => <p className="cur-alert cur-alert--error" role="alert" key={code}>
        {code === 'catalogue_schema_unavailable' ? 'Email catalogue setup is incomplete: the database migration is required before options can be loaded.'
          : `Email catalogue unavailable: ${code.replace(/_/g, ' ')}.`}
      </p>)}
      {state === 'on' && (
        <div className="cur-callout cur-callout--info">
          This workflow is on. Saving creates a new version. The current version keeps running until you approve the new one.
        </div>
      )}
      {view && <p className="cur-hint">Published version {view.current_version ?? 'not yet published'}.
        {view.active_version != null ? ` Active version ${view.active_version}.` : ' No active version.'}
        {view.new_version_waiting ? ' A new version is waiting for explicit approval.' : ''}
      </p>}
      {view?.settings_changed && <p className="cur-callout cur-callout--warn">Its email source or configuration changed. Review each client email before activation.</p>}
      {readOnly && (
        <div className="cur-stack">
          <p>{userReadOnly ? 'Read-only workflow settings.' : 'Made with the old editor. You can pause it or turn it on, but not edit it here.'}</p>
          {view && view.steps && <StepChips steps={view.steps} />}
        </div>
      )}
      {view && <section className="cur-stack" aria-label="Workflow email settings">
        {view.catalogue_needs_review && <p className="cur-problem">Catalogue email settings changed. Review the chosen source version before adopting them.</p>}
        {!userReadOnly && <div className="cur-inline"><label className="cur-field">Email change source<select className="cur-input" value={adoptionVersion} disabled={busy} onChange={event => setAdoptionVersion(event.target.value)}>
          <option value="draft">Draft only</option>
          {view.current_version !== null && <option value={view.current_version}>Current version {view.current_version}</option>}
          {view.active_version !== null && view.active_version !== view.current_version && <option value={view.active_version}>Active version {view.active_version}</option>}
        </select></label><button type="button" className="ui-btn ui-btn--secondary ui-btn--md" disabled={busy} onClick={() => setAdoption({ version: adoptionVersion === 'draft' ? null : Number(adoptionVersion) })}>
          Review catalogue email changes</button></div>}
      </section>}
      {view && adoption && <WorkflowEmailAdoptionReview key={`${view.workflow_id}:${adoption.version}`}
        workflowId={view.workflow_id} version={adoption.version} workflowState={view.state} unsavedChanges={dirty}
        readOnly={userReadOnly} onApplyStart={beginAdoption} onClose={() => { if (!adoptionOwner.current) setAdoption(null); }}
        onApplied={async () => { await loadWorkflow(true); setAdoption(null); onSaved(null); }} />}
      {!readOnly && !loaded && !loadProblem && <p className="cur-hint">Loading…</p>}
      {!readOnly && loaded && step === 1 && (
        <h2 className="cur-sec-title" tabIndex={-1} ref={heading}>What should happen?</h2>
      )}
      {!readOnly && loaded && step === 1 && (
        <StepEditor idPrefix={id} cards={cards} onChange={setCards} options={options} reviewed={reviewed} reviewedCatalogue={reviewedCatalogue}
          onPreview={setPreview} disabled={busy} settingsChanged={Boolean(view && view.settings_changed)} refusal={refusal} />
      )}
      {!readOnly && loaded && step === 2 && (
        <div className="cur-form">
          <h2 className="cur-sec-title" tabIndex={-1} ref={heading}>Check and save</h2>
          <section>
            <h3 className="cur-sec-title">In one sentence</h3>
            <p className="cur-plain">{stepSentence(cards, options)}</p>
          </section>
          <ul className="cur-checklist" aria-label="Before saving">
            <li><span className={checked >= emails ? 'is-ok' : 'is-no'}><Icon name="check" size="sm" /><span className="cur-sr">{checked >= emails ? 'Done: ' : 'Not yet: '}</span></span>{`Every client email is checked (${checked} of ${emails})`}</li>
            <li><span className={stepsOk ? 'is-ok' : 'is-no'}><Icon name="check" size="sm" /><span className="cur-sr">{stepsOk ? 'Done: ' : 'Not yet: '}</span></span>It ends properly</li>
            <li><span className={shownName.trim() ? 'is-ok' : 'is-no'}><Icon name="check" size="sm" /><span className="cur-sr">{shownName.trim() ? 'Done: ' : 'Not yet: '}</span></span>It has a name</li>
          </ul>
          <div className="cur-field">
            <label htmlFor={`${id}-name`}>Name</label>
            <input id={`${id}-name`} className="cur-input" maxLength={120} value={shownName} disabled={busy}
              onChange={(event) => { if (!adoptionOwner.current) { setName(event.target.value); setNameTouched(true); } }} />
          </div>
          <NoteField id={`${id}-note`} value={note} onChange={setNote} disabled={busy} />
          <section>
            <h3 className="cur-sec-title">Used by</h3>
            <p>
              {view && view.used_by.length
                ? `${view.used_by.length} ${view.used_by.length === 1 ? 'example' : 'examples'}`
                  + `${view.followed_last_7_days ? `: the AI followed it ${formatCount(view.followed_last_7_days)} ${view.followed_last_7_days === 1 ? 'time' : 'times'} last week` : ''}.`
                : 'No examples use it yet.'}
            </p>
            <p className="cur-hint">
              Attach it to examples in Teach the AI ·{' '}
              <button type="button" className="cur-linkish" disabled={busy} onClick={() => { onOpenTeach(); onClose(); }}>Go to Teach the AI</button>
            </p>
          </section>
          <SaveProblem message={problem} onReload={busy ? undefined : () => {
            if (lostCreate) { onSaved(null); onClose(); return; }
            setProblem('');
            loadWorkflow(false);
          }} />
        </div>
      )}
      {preview !== null && (
        <EmailPreviewModal onCatalogueChecked={proofs => setReviewedCatalogue(current => ({ ...current, ...proofs }))} cards={cards} index={preview} checked={false} reviewed={reviewed} reviewedCatalogue={reviewedCatalogue} onClose={() => setPreview(null)}
          onChecked={(ticks) => setReviewed((current) => ({ ...current, ...ticks }))} />
      )}
    </Modal>
  );
};
